import {Form, Input, InputNumber, Row, Col, Select, Switch} from 'antd';
import FadeEffect from 'components/animation/fadeEffect';
import plusIcon from 'images/plus-circle.png';
import deleteIcon from 'images/delete.svg';
import {fieldRules} from 'utils/helper';
import styles from '../rewardSchemeTabs/newRewardSchemes.module.scss';
import {
  rewardAssetClass,
  rewardType,
  stockOrderStatus,
  walletCurrencies,
} from 'utils/mock';

export const LeaderboardConfig = ({form, visible, stockOptions = []}) => {
  const leaderboardRewards = Form.useWatch('leaderboardRewards', form) || [];
  const rewardExpirationDurationUnit = Form.useWatch(
    'rewardExpirationDurationUnit',
    form,
  );
  const rewardExpirationDurationValue = Form.useWatch(
    'rewardExpirationDurationValue',
    form,
  );
  const cumulativeDurationUnit = Form.useWatch('cumulativeDurationUnit', form);
  const cumulativeDurationValue = Form.useWatch(
    'cumulativeDurationValue',
    form,
  );

  if (!visible) {
    return null;
  }

  return (
    <div className={styles.configSection}>
      <div className={styles.sectionHeader}>
        <h1>Leaderboard Configuration</h1>
      </div>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Eligible Minimum Transactions"
            name="minNumberOfTransactions"
            tooltip="Minimum number of buy transactions required to be eligible for rewards (e.g., 2 transactions)"
            rules={[
              {
                required: true,
                message: 'Eligible minimum transactions is required',
              },
              {
                type: 'number',
                min: 0,
                message: 'Minimum transactions must be 0 or greater',
              },
            ]}>
            <InputNumber className="input-number" min={0} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Transaction Statuses"
            name="transactionStatuses"
            tooltip="Which transaction statuses count toward eligibility (e.g., ['FILLED', 'SETTLED']). Leave empty to use defaults"
            rules={[
              {
                required: true,
                message: 'Transaction statuses is required',
                type: 'array',
                min: 1,
              },
            ]}>
            <Select mode="tags" placeholder="e.g., FILLED">
              {stockOrderStatus.map(({name, key}) => (
                <Select.Option value={key} key={key}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Min Portfolio Invested Amount (XOF)"
            name="minPortfolioInvestedAmountInXOF"
            tooltip="Minimum total invested amount (cost basis) in XOF required to enter the leaderboard (e.g., 50000 for 50,000 FCFA)"
            rules={[
              {
                required: true,
                message: 'Min portfolio invested amount is required',
              },
              {
                type: 'number',
                min: 0,
                message: 'Portfolio amount must be 0 or greater',
              },
            ]}>
            <InputNumber className="input-number" min={0} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Reward Expiration Duration Unit"
            name="rewardExpirationDurationUnit"
            tooltip="Time unit for reward expiration (e.g., 'WEEKS', 'DAYS', 'MONTHS'). Works with rewardExpirationDurationValue"
            dependencies={['rewardExpirationDurationValue']}
            rules={[
              {
                required: true,
                message: 'Reward expiration duration unit is required',
              },
              {
                validator: (_, value) => {
                  if (
                    (value && !rewardExpirationDurationValue) ||
                    (!value && rewardExpirationDurationValue)
                  ) {
                    // eslint-disable-next-line no-undef
                    return Promise.reject(
                      new Error(
                        'Both duration unit and value must be provided together',
                      ),
                    );
                  }
                  // eslint-disable-next-line no-undef
                  return Promise.resolve();
                },
              },
            ]}>
            <Select allowClear>
              <Select.Option value="DAYS">DAYS</Select.Option>
              <Select.Option value="WEEKS">WEEKS</Select.Option>
              <Select.Option value="MONTHS">MONTHS</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Reward Expiration Duration Value"
            name="rewardExpirationDurationValue"
            tooltip="How long winners have to claim/meet eligibility after cycle ends (e.g., 72 for 72 hours)"
            dependencies={['rewardExpirationDurationUnit']}
            rules={[
              {
                required: true,
                message: 'Reward expiration duration value is required',
              },
              {
                type: 'number',
                min: 0,
                message: 'Duration value must be 0 or greater',
              },
              {
                validator: (_, value) => {
                  if (
                    (value && !rewardExpirationDurationUnit) ||
                    (!value && rewardExpirationDurationUnit)
                  ) {
                    // eslint-disable-next-line no-undef
                    return Promise.reject(
                      new Error(
                        'Both duration unit and value must be provided together',
                      ),
                    );
                  }
                  // eslint-disable-next-line no-undef
                  return Promise.resolve();
                },
              },
            ]}>
            <InputNumber className="input-number" min={0} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Duration Description"
            name="durationDescription"
            tooltip="Human-readable competition schedule shown to users (e.g., 'Weekly: Thursday 00:00 - Wednesday 23:59 GMT')"
            rules={[fieldRules('Duration description')]}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Winners Announced Timeframe"
            name="winnersAnnouncedTimeframe"
            tooltip="When winners will be announced after period ends (e.g., 'Within 48 hours of period end')"
            rules={[fieldRules('Winners announced timeframe')]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        label="Prize Distribution Timeframe"
        name="prizeDistributionTimeframe"
        tooltip="When prizes will be distributed to winners (e.g., 'Within 5 business days')"
        rules={[fieldRules('Prize distribution timeframe')]}>
        <Input />
      </Form.Item>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Cycle Length Unit"
            name="cumulativeDurationUnit"
            tooltip="For leaderboards: defines cycle length (e.g., unit='WEEKS', value=2 means bi-weekly cycles)"
            dependencies={['cumulativeDurationValue']}
            rules={[
              {
                required: true,
                message: 'Cycle length unit is required',
              },
              {
                validator: (_, value) => {
                  if (
                    (value && !cumulativeDurationValue) ||
                    (!value && cumulativeDurationValue)
                  ) {
                    // eslint-disable-next-line no-undef
                    return Promise.reject(
                      new Error(
                        'Both cycle length unit and value must be provided together',
                      ),
                    );
                  }
                  // eslint-disable-next-line no-undef
                  return Promise.resolve();
                },
              },
            ]}>
            <Select allowClear>
              <Select.Option value="DAYS">DAYS</Select.Option>
              <Select.Option value="WEEKS">WEEKS</Select.Option>
              <Select.Option value="MONTHS">MONTHS</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Cycle Length Value"
            name="cumulativeDurationValue"
            tooltip="For leaderboards: defines cycle length (e.g., unit='WEEKS', value=2 means bi-weekly cycles)"
            dependencies={['cumulativeDurationUnit']}
            rules={[
              {
                required: true,
                message: 'Cycle length value is required',
              },
              {
                type: 'number',
                min: 0,
                message: 'Cycle length value must be 0 or greater',
              },
              {
                validator: (_, value) => {
                  if (
                    (value && !cumulativeDurationUnit) ||
                    (!value && cumulativeDurationUnit)
                  ) {
                    // eslint-disable-next-line no-undef
                    return Promise.reject(
                      new Error(
                        'Both cycle length unit and value must be provided together',
                      ),
                    );
                  }
                  // eslint-disable-next-line no-undef
                  return Promise.resolve();
                },
              },
            ]}>
            <InputNumber className="input-number" min={0} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        label="Asset Classes"
        name="assetClass"
        tooltip="Which asset classes count for leaderboard ranking — e.g. Public equity only counts stock portfolio performance"
        rules={[
          {
            required: true,
            message: 'At least one asset class is required',
            type: 'array',
            min: 1,
          },
        ]}>
        {/* Was a second, hardcoded copy of the asset-class list carrying the
            retired STOCKS / VENTURE / VENTURE_FUND values. Sharing the constant
            means the next vocabulary change lands in one place. */}
        <Select mode="multiple" allowClear>
          {rewardAssetClass.map(({key, name}) => (
            <Select.Option value={key} key={key}>
              {name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Is Recurring"
            name="isRecurring"
            tooltip="Whether the competition repeats automatically (e.g., every week). When true, a new reward scheme is created for each cycle"
            valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Require Daba Pro"
            name="requireDabaPro"
            tooltip="Whether the competition requires users to be Daba Pro to win (e.g., true or false)"
            valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Ranking Method"
            name="rankingMethod"
            tooltip="How users are ranked. Currently only 'PORTFOLIO_PERCENTAGE_CHANGE' is supported (ROI improvement during cycle)"
            rules={[fieldRules('Ranking method')]}>
            <Select>
              <Select.Option value="PORTFOLIO_PERCENTAGE_CHANGE">
                Portfolio Percentage Change
              </Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Tie Breaker"
            name="tieBreaker"
            tooltip="How to break ties when users have the same ROI change (e.g., 'HIGHEST_PORTFOLIO_VALUE', 'EARLIEST_ENTRY')"
            rules={[fieldRules('Tie breaker')]}>
            <Select allowClear>
              {tieBreakerOptions.map(({label, value}) => (
                <Select.Option value={value} key={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Form.List name="leaderboardRewards">
        {(fields, {add, remove}) => (
          <>
            {fields.map(({key, name: fieldName}) => {
              const showCashFields =
                leaderboardRewards[fieldName]?.rewardType === 'CASH_TO_INVEST';
              const showStockFields =
                leaderboardRewards[fieldName]?.rewardType === 'STOCKS';
              const showFeeWaiverFields =
                leaderboardRewards[fieldName]?.rewardType ===
                'TRANSACTION_FEE_WAIVER';

              return (
                <FadeEffect key={key}>
                  <div className={styles['key-facts']}>
                    <div>
                      <Form.Item
                        label="Reward"
                        name={[fieldName, 'rewardType']}
                        rules={[fieldRules('reward is required')]}>
                        <Select allowClear>
                          {rewardType
                            .filter(
                              reward =>
                                reward.key !== 'DABA_PRO_ONE_MONTH' &&
                                reward.key !== 'NONE',
                            )
                            .map(({key: rewardKey, name}) => (
                              <Select.Option value={rewardKey} key={rewardKey}>
                                {name}
                              </Select.Option>
                            ))}
                        </Select>
                      </Form.Item>
                      {showCashFields && (
                        <>
                          <Form.Item
                            label="Cash Amount"
                            name={[fieldName, 'reward', 'cash', 'amount']}
                            rules={[
                              {
                                required: true,
                                message: 'Cash amount is required',
                                type: 'number',
                                min: 0,
                              },
                            ]}>
                            <InputNumber className="input-number" min={0} />
                          </Form.Item>
                          <Form.Item
                            label="Cash Currency"
                            name={[fieldName, 'reward', 'cash', 'currency']}
                            rules={[fieldRules('Cash currency')]}>
                            <Select allowClear>
                              {walletCurrencies.map(({key, name}) => (
                                <Select.Option value={key} key={key}>
                                  {name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </>
                      )}
                      {showStockFields && (
                        <>
                          <Form.Item
                            label="Units"
                            name={[
                              fieldName,
                              'reward',
                              'instruments',
                              'rewardUnits',
                            ]}
                            rules={[
                              {
                                required: true,
                                message: 'Units is required',
                                type: 'number',
                                min: 0,
                              },
                            ]}>
                            <InputNumber className="input-number" min={0} />
                          </Form.Item>
                          {/* Was a free-text ticker. The tier input takes an
                              instrument id, and a ticker is no longer unique
                              across exchanges, so it is picked from the reward
                              banks rather than typed. */}
                          <Form.Item
                            label="Instrument"
                            name={[
                              fieldName,
                              'reward',
                              'instruments',
                              'instrumentId',
                            ]}
                            rules={[fieldRules('Instrument')]}>
                            <Select
                              showSearch
                              allowClear
                              optionFilterProp="children">
                              {stockOptions.map((item, index) => (
                                <Select.Option
                                  // A sentinel, so options with no instrument
                                  // cannot all share `undefined` and match an
                                  // unset field.
                                  value={
                                    item?.instrumentId ??
                                    `unlinked:${item?.id ?? index}`
                                  }
                                  disabled={!item?.instrumentId}
                                  key={item?.id ?? `${item?.ticker}-${index}`}>
                                  {item?.ticker}
                                  {item?.exchangeCode
                                    ? ` · ${item.exchangeCode}`
                                    : ''}
                                  {item?.instrumentId
                                    ? ''
                                    : ' — no instrument linked'}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </>
                      )}
                      {showFeeWaiverFields && (
                        <>
                          <Form.Item
                            label="Fee Waiver Percentage"
                            name={[
                              fieldName,
                              'reward',
                              'transactionFeeWaiver',
                              'percentage',
                            ]}
                            rules={[
                              {
                                required: true,
                                message: 'Fee waiver percentage is required',
                                type: 'number',
                                min: 0,
                                max: 100,
                              },
                            ]}>
                            <InputNumber
                              className="input-number"
                              min={0}
                              max={100}
                            />
                          </Form.Item>
                          <Form.List
                            name={[
                              fieldName,
                              'reward',
                              'transactionFeeWaiver',
                              'maxFeeWaiverConfig',
                            ]}>
                            {(feeFields, {add, remove}) => (
                              <>
                                {feeFields.map(
                                  ({key: feeKey, name: feeName}) => (
                                    <div
                                      className={styles['key-facts']}
                                      key={feeKey}>
                                      <div>
                                        <Form.Item
                                          label="Currency"
                                          name={[feeName, 'currency']}
                                          rules={[
                                            fieldRules('currency is required'),
                                          ]}>
                                          <Select allowClear>
                                            {walletCurrencies.map(
                                              ({key, name}) => (
                                                <Select.Option
                                                  value={key}
                                                  key={key}>
                                                  {name}
                                                </Select.Option>
                                              ),
                                            )}
                                          </Select>
                                        </Form.Item>
                                        <Form.Item
                                          label="Value"
                                          name={[feeName, 'value']}
                                          rules={[
                                            fieldRules('value is required'),
                                          ]}>
                                          <InputNumber
                                            className="input-number"
                                            min={0}
                                          />
                                        </Form.Item>
                                      </div>
                                      <img
                                        onClick={() => remove(feeName)}
                                        src={deleteIcon}
                                        alt=""
                                      />
                                    </div>
                                  ),
                                )}
                                <div
                                  onClick={() => add({currency: '', value: ''})}
                                  className={styles['add-button']}>
                                  <img src={plusIcon} alt="" />
                                  <h1>Add Fee Waiver</h1>
                                </div>
                              </>
                            )}
                          </Form.List>
                        </>
                      )}
                      {!showCashFields &&
                        !showStockFields &&
                        !showFeeWaiverFields && (
                          <span className={styles.warning}>
                            Select a reward type to configure its fields
                          </span>
                        )}
                    </div>
                    <img
                      onClick={() => remove(fieldName)}
                      src={deleteIcon}
                      alt=""
                    />
                  </div>
                </FadeEffect>
              );
            })}
            <div
              onClick={() => add({position: '', rewardType: ''})}
              className={styles['add-button']}>
              <img src={plusIcon} alt="" />
              <h1>Add Leaderboard Row</h1>
            </div>
          </>
        )}
      </Form.List>
      <Form.Item
        label="Disclaimer"
        name="disclaimer"
        tooltip="Legal disclaimer shown to users (e.g., rules about disqualification for market manipulation, wash trading, or suspicious activity)"
        rules={[fieldRules('Disclaimer')]}>
        <Input.TextArea rows={2} />
      </Form.Item>
    </div>
  );
};

const tieBreakerOptions = [
  {
    label: 'Highest Portfolio Value',
    value: 'HIGHEST_PORTFOLIO_VALUE',
  },
  {
    label: 'Lowest Portfolio Value',
    value: 'LOWEST_PORTFOLIO_VALUE',
  },
  {
    label: 'Most Transactions',
    value: 'MOST_TRANSACTIONS',
  },
  {
    label: 'Earliest First Transaction',
    value: 'EARLIEST_FIRST_TRANSACTION',
  },
  {
    label: 'Latest First Transaction',
    value: 'LATEST_FIRST_TRANSACTION',
  },
];
